import { useRef } from 'react'
import type { Quote_ } from 'src/api/db/apiQuote'
import type { QuoteItem_ } from 'src/api/db/apiQuoteItem'
import { OrderTable } from 'src/shared/components/OrderTable'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Card } from 'src/shared/components/ui'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import type { BillingMode } from './types'
import { AdminPaymentFields } from './components/AdminPaymentFields'
import { DocumentHeader } from './components/DocumentHeader'
import { InvoiceActions } from './components/InvoiceActions'
import { InvoicePaymentDetails } from './components/InvoicePaymentDetails'

export const BillingDocument = ({
  quote,
  quoteItems,
  publicId,
  mode = 'quote',
  isReadOnly = false,
  embedded = false,
}: {
  quote: Quote_
  quoteItems?: QuoteItem_[]
  publicId?: string
  mode?: BillingMode
  isReadOnly?: boolean
  /** Skip nested Card panel when already inside a checkout step. */
  embedded?: boolean
}) => {
  const { t } = useLocalization()
  const isAdmin = useIsAdmin()
  const documentRef = useRef<HTMLDivElement>(null)

  return (
    <Column className="mx-auto w-full max-w-4xl shrink-0 gap-4">
      {mode === 'invoice' && !isReadOnly && (
        <Row className="justify-end items-start gap-2">
          <InvoiceActions quote={quote} documentRef={documentRef} />
        </Row>
      )}

      {isAdmin && !isReadOnly && (
        <Column className="gap-2 rounded-card border border-border bg-muted/30 px-4 py-3.5">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('Payment settings (admin)', 'Betalningsinställningar')}
          </span>
          <AdminPaymentFields quote={quote} />
        </Column>
      )}

      <Card
        ref={documentRef}
        variant={embedded ? 'plain' : 'panel'}
        wrapperClassName={!embedded ? '!shadow-sm' : undefined}
        className="w-full border border-gray-200/80 px-8 py-8 !shadow-none hover:!shadow-none"
      >
        <Column className="gap-8">
          <DocumentHeader mode={mode} quote={quote} isAdmin={isAdmin} />

          <OrderTable
            quote_id={quote.id}
            quotePublicId={publicId}
            quoteStatus={quote.status}
            quoteItems={quoteItems}
            isReadOnly={isReadOnly}
          />

          {mode === 'invoice' && <InvoicePaymentDetails quote={quote} />}
        </Column>
      </Card>
    </Column>
  )
}
